import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { OrderDoc, PaginatedOrders } from './ordersSlice';

/** Status buckets exposed on the customer details screen. `all` is the
 * unfiltered view; the rest map 1:1 to the `?status=` query param on
 * `GET /bp_owner_get_customer_orders_information/:customerId`. */
export type CustomerOrdersTab = 'all' | 'completed' | 'partial' | 'pending';

const ALL_TABS: CustomerOrdersTab[] = [
  'all',
  'completed',
  'partial',
  'pending',
];

const STATUS_TABS: CustomerOrdersTab[] = ['completed', 'partial', 'pending'];

export interface CustomerOrdersEntry {
  all: PaginatedOrders;
  completed: PaginatedOrders;
  partial: PaginatedOrders;
  pending: PaginatedOrders;
  loading: Record<CustomerOrdersTab, boolean>;
  error: Record<CustomerOrdersTab, string | null>;
}

export interface CustomerOrdersState {
  byCustomerId: Record<string, CustomerOrdersEntry>;
}

const createInitialPage = (): PaginatedOrders => ({
  docs: [],
  hasNextPage: false,
  hasPrevPage: false,
  limit: 50,
  nextPage: 1,
  page: 0,
  perPage: 50,
  totalDocs: 0,
  totalPages: 0,
});

const createInitialEntry = (): CustomerOrdersEntry => ({
  all: createInitialPage(),
  completed: createInitialPage(),
  partial: createInitialPage(),
  pending: createInitialPage(),
  loading: { all: false, completed: false, partial: false, pending: false },
  error: { all: null, completed: null, partial: null, pending: null },
});

const initialState: CustomerOrdersState = {
  byCustomerId: {},
};

const ensureEntry = (
  state: CustomerOrdersState,
  customerId: string,
): CustomerOrdersEntry => {
  if (!state.byCustomerId[customerId]) {
    state.byCustomerId[customerId] = createInitialEntry();
  }
  return state.byCustomerId[customerId];
};

/** Reused from backoffice/ordersSlice: dedupe by `bp_order_id`, prefer the
 * newer `bp_order_master_updated_at`, sort desc. */
const mergeOrders = (
  existing: OrderDoc[],
  incoming: OrderDoc[],
): OrderDoc[] => {
  const map = new Map<string, OrderDoc>();
  [...existing, ...incoming].forEach(order => {
    if (!order?.bp_order_id) return;
    const prev = map.get(order.bp_order_id);
    if (
      !prev ||
      new Date(order.bp_order_master_updated_at).getTime() >
        new Date(prev.bp_order_master_updated_at).getTime()
    ) {
      map.set(order.bp_order_id, { ...order });
    }
  });
  return Array.from(map.values()).sort(
    (a, b) =>
      new Date(b.bp_order_master_updated_at).getTime() -
      new Date(a.bp_order_master_updated_at).getTime(),
  );
};

/** Which status bucket does this order belong to? Matches
 * `backofficeOrders.upsertOrderDoc` polarity. */
const tabForStatus = (
  status: string,
): Exclude<CustomerOrdersTab, 'all'> => {
  const lower = String(status || '').toLowerCase();
  if (lower.includes('partial')) return 'partial';
  if (lower === 'completed') return 'completed';
  return 'pending';
};

const removeOrderIdFrom = (docs: OrderDoc[], orderId: string): OrderDoc[] =>
  docs.filter(o => o.bp_order_id !== orderId);

const customerOrdersSlice = createSlice({
  name: 'backofficeCustomerOrders',
  initialState,
  reducers: {
    setCustomerOrdersLoading: (
      state,
      action: PayloadAction<{
        customerId: string;
        tab: CustomerOrdersTab;
        loading: boolean;
      }>,
    ) => {
      const entry = ensureEntry(state, action.payload.customerId);
      entry.loading[action.payload.tab] = action.payload.loading;
      if (action.payload.loading) {
        entry.error[action.payload.tab] = null;
      }
    },
    setCustomerOrdersError: (
      state,
      action: PayloadAction<{
        customerId: string;
        tab: CustomerOrdersTab;
        error: string | null;
      }>,
    ) => {
      const entry = ensureEntry(state, action.payload.customerId);
      entry.error[action.payload.tab] = action.payload.error;
      entry.loading[action.payload.tab] = false;
    },
    /** Append/merge a page into the target bucket. `payload` matches the
     * `PaginatedOrders` normalizer in `parseCustomerOrdersResponse`. */
    addCustomerOrders: (
      state,
      action: PayloadAction<{
        customerId: string;
        tab: CustomerOrdersTab;
        payload: PaginatedOrders;
      }>,
    ) => {
      const entry = ensureEntry(state, action.payload.customerId);
      const bucket = entry[action.payload.tab];
      const merged = mergeOrders(bucket.docs, action.payload.payload.docs);
      entry[action.payload.tab] = {
        ...bucket,
        ...action.payload.payload,
        docs: merged,
      };
    },
    /** Reset a single bucket (used by pull-to-refresh before page 1). */
    clearCustomerOrdersTab: (
      state,
      action: PayloadAction<{
        customerId: string;
        tab: CustomerOrdersTab;
      }>,
    ) => {
      const entry = ensureEntry(state, action.payload.customerId);
      entry[action.payload.tab] = createInitialPage();
      entry.error[action.payload.tab] = null;
    },
    /** Drop a single customer entry. */
    clearCustomerOrders: (state, action: PayloadAction<string>) => {
      delete state.byCustomerId[action.payload];
    },
    /** Drop all customer order entries. Called on location switch — same
     * pattern as `clearSuppliersState`. */
    clearAllCustomerOrders: () => initialState,
    /** SSE fan-out: patch every loaded customer whose docs already include
     * this order id, or whose customer id matches when the server returns
     * `bp_order_customer_id` on the doc. Reconciles `totalDocs` per bucket
     * so sub-tab counts stay consistent with the visible list across status
     * transitions (e.g. pending → completed) and net-new inserts.
     *
     * `totalDocs` is only mutated for buckets that have already been loaded
     * (`page > 0`). Unloaded buckets keep 0 until their first fetch — the
     * screen hides counts for unloaded buckets, and inventing a value here
     * would race the server-authoritative totalDocs from that first page. */
    upsertOrderInCustomerSlices: (
      state,
      action: PayloadAction<OrderDoc>,
    ) => {
      const updated = action.payload;
      if (!updated?.bp_order_id) return;
      const targetTab = tabForStatus(updated.bp_order_master_order_status);
      // The stream doc may include a customer id at runtime even though the
      // TS type does not declare it — cart creation sends `bp_order_customer_id`.
      const orderCustomerId = String(
        (updated as any)?.bp_order_customer_id || '',
      ).trim();

      for (const [customerId, entry] of Object.entries(state.byCustomerId)) {
        const idMatches = orderCustomerId && orderCustomerId === customerId;
        const wasIn: Record<CustomerOrdersTab, boolean> = {
          all: entry.all.docs.some(o => o.bp_order_id === updated.bp_order_id),
          completed: entry.completed.docs.some(
            o => o.bp_order_id === updated.bp_order_id,
          ),
          partial: entry.partial.docs.some(
            o => o.bp_order_id === updated.bp_order_id,
          ),
          pending: entry.pending.docs.some(
            o => o.bp_order_id === updated.bp_order_id,
          ),
        };
        const previouslyLoaded = ALL_TABS.some(t => wasIn[t]);
        if (!idMatches && !previouslyLoaded) continue;

        // Remove from every bucket so we never duplicate across tabs.
        for (const t of ALL_TABS) {
          entry[t] = {
            ...entry[t],
            docs: removeOrderIdFrom(entry[t].docs, updated.bp_order_id),
          };
        }

        // Insert into the status bucket + the `all` bucket.
        entry[targetTab] = {
          ...entry[targetTab],
          docs: mergeOrders(entry[targetTab].docs, [updated]),
        };
        entry.all = {
          ...entry.all,
          docs: mergeOrders(entry.all.docs, [updated]),
        };

        // Reconcile totalDocs (loaded buckets only).
        if (entry.all.page > 0 && !wasIn.all) {
          entry.all.totalDocs = entry.all.totalDocs + 1;
        }
        for (const bucket of STATUS_TABS) {
          if (entry[bucket].page === 0) continue;
          const shouldBeIn = bucket === targetTab;
          if (wasIn[bucket] && !shouldBeIn) {
            entry[bucket].totalDocs = Math.max(0, entry[bucket].totalDocs - 1);
          } else if (!wasIn[bucket] && shouldBeIn) {
            entry[bucket].totalDocs = entry[bucket].totalDocs + 1;
          }
        }
      }
    },
    /** SSE fan-out for deletes. */
    removeOrderFromCustomerSlices: (
      state,
      action: PayloadAction<string>,
    ) => {
      const orderId = String(action.payload || '').trim();
      if (!orderId) return;
      for (const entry of Object.values(state.byCustomerId)) {
        for (const t of ALL_TABS) {
          const filtered = removeOrderIdFrom(entry[t].docs, orderId);
          if (filtered.length !== entry[t].docs.length) {
            entry[t] = {
              ...entry[t],
              docs: filtered,
              totalDocs: Math.max(0, entry[t].totalDocs - 1),
            };
          }
        }
      }
    },
  },
});

export const {
  setCustomerOrdersLoading,
  setCustomerOrdersError,
  addCustomerOrders,
  clearCustomerOrdersTab,
  clearCustomerOrders,
  clearAllCustomerOrders,
  upsertOrderInCustomerSlices,
  removeOrderFromCustomerSlices,
} = customerOrdersSlice.actions;

export { ALL_TABS as CUSTOMER_ORDER_TABS, STATUS_TABS as CUSTOMER_STATUS_TABS };

export default customerOrdersSlice.reducer;
